import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { UserPlus, Mail, Phone, MapPin, CreditCard, User, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateLocker = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [nextLocker, setNextLocker] = useState('');
  const [useManualLocker, setUseManualLocker] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    country_code: '+58',
    cedula: '',
    customer_type: 'publico',
    manual_locker_code: '', // Nuevo campo para casillero manual
    // Dirección en USA
    address_usa: '',
    city_usa: 'Miami',
    state_usa: 'FL',
    postal_code_usa: '33166',
    // Dirección de entrega en Venezuela
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    notes: ''
  });

  const axiosConfig = {
    headers: { 'Authorization': `Bearer ${token}` }
  };

  // Obtener el siguiente número de casillero automático
  useEffect(() => {
    const fetchNextLocker = async () => {
      try {
        const res = await axios.get(`${API}/admin/clients/next-locker`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setNextLocker(res.data.next_locker);
      } catch (err) {
      }
    };
    if (token) {
      fetchNextLocker();
    }
  }, [success, token]); // Recargar después de crear uno nuevo

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validaciones básicas
    if (!formData.full_name || !formData.email || !formData.password) {
      setError('Nombre, email y contraseña son obligatorios');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/clients`, {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        country_code: formData.country_code,
        cedula: formData.cedula,
        customer_type: formData.customer_type,
        manual_locker_code: useManualLocker ? formData.manual_locker_code : null, // Enviar casillero manual
        address: formData.address_usa,
        city: formData.city_usa,
        state: formData.state_usa,
        postal_code: formData.postal_code_usa,
        delivery_address: formData.delivery_address,
        delivery_city: formData.delivery_city,
        delivery_state: formData.delivery_state,
        notes: formData.notes
      }, axiosConfig);

      setSuccess({
        message: 'Cliente y casillero creados exitosamente',
        locker_id: response.data.locker_id,
        client_id: response.data.id || response.data.client_id
      });

      // Limpiar formulario
      setFormData({
        full_name: '',
        email: '',
        password: '',
        phone: '',
        country_code: '+58',
        cedula: '',
        customer_type: 'publico',
        manual_locker_code: '',
        address_usa: '',
        city_usa: 'Miami',
        state_usa: 'FL',
        postal_code_usa: '33166',
        delivery_address: '',
        delivery_city: '',
        delivery_state: '',
        notes: ''
      });
      setUseManualLocker(false);

    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <UserPlus size={24} />
            Crear Nuevo Casillero / Cliente
          </CardTitle>
          <CardDescription className="text-blue-100">
            Registra un nuevo cliente y asígnale automáticamente un número de casillero
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-green-800 font-semibold mb-2">✅ {success.message}</h3>
              <p className="text-green-700">
                <strong>Número de Casillero:</strong> {success.locker_id || 'Asignado'}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">❌ {error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Personal */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User size={18} className="text-blue-600" />
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Nombre Completo *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Ej: Juan Pérez"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cedula">Cédula / Documento ID</Label>
                  <Input
                    id="cedula"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                    placeholder="Ej: V-12345678"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Contacto */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Mail size={18} className="text-blue-600" />
                Información de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Correo Electrónico *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="cliente@email.com"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Mínimo 6 caracteres"
                    required
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="w-24">
                    <Label htmlFor="country_code">Código</Label>
                    <Select
                      value={formData.country_code}
                      onValueChange={(v) => setFormData({...formData, country_code: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+58">+58 VE</SelectItem>
                        <SelectItem value="+1">+1 US</SelectItem>
                        <SelectItem value="+57">+57 CO</SelectItem>
                        <SelectItem value="+34">+34 ES</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Ej: 4121234567"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="customer_type">Tipo de Cliente</Label>
                  <Select
                    value={formData.customer_type}
                    onValueChange={(v) => setFormData({...formData, customer_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publico">Público</SelectItem>
                      <SelectItem value="alumno">Alumnos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Dirección de Entrega en Venezuela */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <MapPin size={18} className="text-blue-600" />
                Dirección de Entrega (Venezuela)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="delivery_address">Dirección Completa</Label>
                  <Textarea
                    id="delivery_address"
                    name="delivery_address"
                    value={formData.delivery_address}
                    onChange={handleChange}
                    placeholder="Calle, Edificio/Casa, Urbanización, Punto de referencia"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="delivery_city">Ciudad</Label>
                  <Input
                    id="delivery_city"
                    name="delivery_city"
                    value={formData.delivery_city}
                    onChange={handleChange}
                    placeholder="Ej: Caracas"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="delivery_state">Estado</Label>
                  <Input
                    id="delivery_state"
                    name="delivery_state"
                    value={formData.delivery_state}
                    onChange={handleChange}
                    placeholder="Ej: Distrito Capital"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Número de Casillero */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Hash size={18} className="text-blue-600" />
                Número de Casillero
              </h3>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-blue-800">Próximo número automático:</p>
                    <p className="text-2xl font-bold text-blue-600 font-mono">{nextLocker || 'Cargando...'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="useManualLocker"
                      checked={useManualLocker}
                      onCheckedChange={(checked) => setUseManualLocker(checked)}
                    />
                    <Label htmlFor="useManualLocker" className="text-sm font-medium cursor-pointer">
                      Asignar número manualmente
                    </Label>
                  </div>
                </div>
                
                {useManualLocker && (
                  <div className="mt-4 p-3 bg-white rounded border-2 border-blue-300">
                    <Label htmlFor="manual_locker_code" className="text-blue-800 font-semibold">
                      Número de Casillero Manual *
                    </Label>
                    <Input
                      id="manual_locker_code"
                      name="manual_locker_code"
                      value={formData.manual_locker_code}
                      onChange={handleChange}
                      placeholder="Ej: TDA000150"
                      className="mt-2 font-mono text-lg"
                      required={useManualLocker}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formato recomendado: TDA + 6 dígitos (ej: TDA000150)
                    </p>
                  </div>
                )}
                
                {!useManualLocker && (
                  <p className="text-sm text-blue-600 mt-2">
                    ℹ️ El sistema asignará automáticamente el número <strong>{nextLocker}</strong> a este cliente.
                  </p>
                )}
              </div>
            </div>

            {/* Dirección del Casillero (USA) */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <CreditCard size={18} className="text-blue-600" />
                Dirección del Casillero (USA)
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  Esta será la dirección asignada al cliente para recibir sus paquetes en USA.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <Label htmlFor="address_usa">Dirección</Label>
                    <Input
                      id="address_usa"
                      name="address_usa"
                      value={formData.address_usa}
                      onChange={handleChange}
                      placeholder="Se completará con el casillero asignado"
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city_usa">Ciudad</Label>
                    <Input
                      id="city_usa"
                      name="city_usa"
                      value={formData.city_usa}
                      onChange={handleChange}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state_usa">Estado</Label>
                    <Input
                      id="state_usa"
                      name="state_usa"
                      value={formData.state_usa}
                      onChange={handleChange}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code_usa">Código Postal</Label>
                    <Input
                      id="postal_code_usa"
                      name="postal_code_usa"
                      value={formData.postal_code_usa}
                      onChange={handleChange}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notas */}
            <div>
              <Label htmlFor="notes">Notas Adicionales</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Observaciones sobre el cliente..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFormData({
                    full_name: '',
                    email: '',
                    password: '',
                    phone: '',
                    country_code: '+58',
                    cedula: '',
                    customer_type: 'publico',
                    manual_locker_code: '',
                    address_usa: '',
                    city_usa: 'Miami',
                    state_usa: 'FL',
                    postal_code_usa: '33166',
                    delivery_address: '',
                    delivery_city: '',
                    delivery_state: '',
                    notes: ''
                  });
                  setUseManualLocker(false);
                }}
              >
                Limpiar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Creando...' : '✓ Crear Cliente y Casillero'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateLocker;
